import { lumi } from '@/lib/lumi'

const MAX_FILE_SIZE = 10 * 1024 * 1024
const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/jpg']
const VIDEO_TYPES = ['video/mp4', 'video/webm']
const NEWS_BANNER_TYPES = [...ALLOWED_TYPES, ...VIDEO_TYPES]

type UploadResult = {
  fileUrl?: string
  uploadError?: string
}

function asResultList(results: unknown): UploadResult[] {
  if (Array.isArray(results)) return results as UploadResult[]
  if (results && typeof results === 'object') return [results as UploadResult]
  return []
}

function dataUrlToFile(dataUrl: string, name: string) {
  const [header, body] = dataUrl.split(',')
  const mime = header.match(/data:(.*?);/)?.[1] || 'image/jpeg'
  const binary = atob(body || '')
  const bytes = new Uint8Array(binary.length)
  for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i)
  const base = name.replace(/\.[^.]+$/, '') || 'imagem'
  return new File([bytes], `${base}.jpg`, { type: mime })
}

function readOrientation(buffer: ArrayBuffer) {
  const view = new DataView(buffer)
  if (view.byteLength < 12 || view.getUint16(0, false) !== 0xffd8) return 1
  let offset = 2
  while (offset + 8 < view.byteLength) {
    const marker = view.getUint16(offset, false)
    offset += 2
    if (marker === 0xffe1) {
      if (view.getUint32(offset + 2, false) !== 0x45786966) return 1
      const little = view.getUint16(offset + 8, false) === 0x4949
      const start = offset + 8
      const ifd = start + view.getUint32(start + 4, little)
      const entries = view.getUint16(ifd, little)
      for (let i = 0; i < entries; i += 1) {
        const entry = ifd + 2 + i * 12
        if (view.getUint16(entry, little) === 0x0112) {
          return view.getUint16(entry + 8, little) || 1
        }
      }
      return 1
    }
    if ((marker & 0xff00) !== 0xff00) break
    offset += view.getUint16(offset, false)
  }
  return 1
}

function applyOrientation(context: CanvasRenderingContext2D, orientation: number, srcW: number, srcH: number) {
  switch (orientation) {
    case 2:
      context.setTransform(-1, 0, 0, 1, srcW, 0)
      break
    case 3:
      context.setTransform(-1, 0, 0, -1, srcW, srcH)
      break
    case 4:
      context.setTransform(1, 0, 0, -1, 0, srcH)
      break
    case 5:
      context.setTransform(0, 1, 1, 0, 0, 0)
      break
    case 6:
      context.setTransform(0, 1, -1, 0, srcH, 0)
      break
    case 7:
      context.setTransform(0, -1, -1, 0, srcH, srcW)
      break
    case 8:
      context.setTransform(0, -1, 1, 0, 0, srcW)
      break
    default:
      context.setTransform(1, 0, 0, 1, 0, 0)
      break
  }
}

function orientToCanvas(image: HTMLImageElement, orientation: number) {
  const srcW = Math.max(1, image.naturalWidth || image.width || 1)
  const srcH = Math.max(1, image.naturalHeight || image.height || 1)
  const swapped = orientation >= 5 && orientation <= 8
  const canvas = document.createElement('canvas')
  canvas.width = swapped ? srcH : srcW
  canvas.height = swapped ? srcW : srcH
  const context = canvas.getContext('2d')
  if (!context) throw new Error('Não foi possível processar a imagem.')
  applyOrientation(context, orientation, srcW, srcH)
  context.drawImage(image, 0, 0, srcW, srcH)
  context.setTransform(1, 0, 0, 1, 0, 0)
  return canvas
}

function coverToCanvas(source: HTMLCanvasElement, targetW: number, targetH: number, quality: number) {
  const canvas = document.createElement('canvas')
  canvas.width = targetW
  canvas.height = targetH
  const context = canvas.getContext('2d')
  if (!context) throw new Error('Não foi possível processar a imagem.')
  context.fillStyle = '#0b2ea8'
  context.fillRect(0, 0, targetW, targetH)
  context.imageSmoothingEnabled = true
  context.imageSmoothingQuality = 'high'
  const scale = Math.max(targetW / source.width, targetH / source.height)
  const drawW = source.width * scale
  const drawH = source.height * scale
  const dx = (targetW - drawW) / 2
  const dy = (targetH - drawH) / 2
  context.drawImage(source, dx, dy, drawW, drawH)
  return canvasToJpeg(canvas, quality)
}

function loadImage(file: File) {
  return new Promise<{ image: HTMLImageElement; orientation: number }>((resolve, reject) => {
    const image = new Image()
    const objectUrl = URL.createObjectURL(file)
    const finish = () => URL.revokeObjectURL(objectUrl)
    image.onload = async () => {
      let orientation = 1
      try {
        orientation = readOrientation(await file.arrayBuffer())
      } catch {
        orientation = 1
      }
      finish()
      resolve({ image, orientation })
    }
    image.onerror = () => {
      finish()
      reject(new Error('Imagem inválida.'))
    }
    image.src = objectUrl
  })
}

function canvasToJpeg(canvas: HTMLCanvasElement, quality: number) {
  return canvas.toDataURL('image/jpeg', quality)
}

async function processImage(
  file: File,
  options: { maxEdge?: number; ratio?: number; quality?: number } = {},
) {
  const { image, orientation } = await loadImage(file)
  const oriented = orientToCanvas(image, orientation)
  const quality = options.quality ?? 0.86

  if (options.ratio) {
    const targetW = 1600
    const targetH = Math.round(targetW / options.ratio)
    return coverToCanvas(oriented, targetW, targetH, quality)
  }

  const maxEdge = options.maxEdge ?? 1600
  const scale = Math.min(1, maxEdge / Math.max(oriented.width, oriented.height))
  const outW = Math.max(1, Math.round(oriented.width * scale))
  const outH = Math.max(1, Math.round(oriented.height * scale))
  const canvas = document.createElement('canvas')
  canvas.width = outW
  canvas.height = outH
  const context = canvas.getContext('2d')
  if (!context) throw new Error('Não foi possível processar a imagem.')
  context.imageSmoothingEnabled = true
  context.imageSmoothingQuality = 'high'
  context.drawImage(oriented, 0, 0, outW, outH)
  return canvasToJpeg(canvas, quality)
}

async function uploadPrepared(file: File, prepared?: string) {
  const payload = prepared ? dataUrlToFile(prepared, file.name) : file
  try {
    const results = await lumi.tools.file.upload([payload])
    const first = asResultList(results)[0]
    if (first?.fileUrl) return first.fileUrl
    console.error('Upload OSS sem URL, a guardar a foto no cadastro:', first?.uploadError)
  } catch (error) {
    console.error('Falha no upload OSS, a guardar a foto no cadastro:', error)
  }
  if (prepared) return prepared
  throw new Error('Não foi possível enviar a imagem.')
}

export async function uploadImage(file: File, maxEdge = 1600): Promise<string> {
  if (!file) throw new Error('Selecione uma imagem.')
  if (file.size > MAX_FILE_SIZE) throw new Error('A imagem deve ter no máximo 10MB.')
  if (file.type && !ALLOWED_TYPES.includes(file.type) && !file.type.startsWith('image/')) {
    throw new Error('Tipo de ficheiro inválido. Envie JPG, PNG, GIF ou WEBP.')
  }
  const prepared = await processImage(file, { maxEdge })
  return uploadPrepared(file, prepared)
}

export async function uploadNewsImage(file: File): Promise<string> {
  if (!file) throw new Error('Selecione uma imagem.')
  if (file.size > MAX_FILE_SIZE) throw new Error('A imagem deve ter no máximo 10MB.')
  if (file.type && !ALLOWED_TYPES.includes(file.type) && !file.type.startsWith('image/')) {
    throw new Error('Tipo de ficheiro inválido. Envie JPG, PNG, GIF ou WEBP.')
  }
  const prepared = await processImage(file, { ratio: 16 / 9, quality: 0.88 })
  return uploadPrepared(file, prepared)
}

function isVideoFile(file: File) {
  const type = (file.type || '').toLowerCase()
  const name = (file.name || '').toLowerCase()
  return VIDEO_TYPES.includes(type) || name.endsWith('.mp4') || name.endsWith('.webm')
}

function isAnimatedOrVideo(file: File) {
  const type = (file.type || '').toLowerCase()
  const name = (file.name || '').toLowerCase()
  return isVideoFile(file) || type === 'image/gif' || name.endsWith('.gif')
}

export async function uploadNewsBannerMedia(file: File): Promise<{ url: string; mediaType: 'image' | 'video' }> {
  if (!file) throw new Error('Selecione um ficheiro.')
  if (file.size > MAX_FILE_SIZE) throw new Error('O ficheiro deve ter no máximo 10MB.')
  const type = (file.type || '').toLowerCase()
  const name = (file.name || '').toLowerCase()
  const allowed =
    NEWS_BANNER_TYPES.includes(type) ||
    type.startsWith('image/') ||
    name.endsWith('.jpg') ||
    name.endsWith('.jpeg') ||
    name.endsWith('.png') ||
    name.endsWith('.gif') ||
    name.endsWith('.webp') ||
    name.endsWith('.mp4') ||
    name.endsWith('.webm')
  if (!allowed) {
    throw new Error('Tipo inválido. Envie JPEG, PNG, GIF, MP4 ou WEBM.')
  }

  const mediaType: 'image' | 'video' = isVideoFile(file) ? 'video' : 'image'

  try {
    const results = await lumi.tools.file.upload([file])
    const first = asResultList(results)[0]
    if (first?.fileUrl) return { url: first.fileUrl, mediaType }
    console.error('Upload OSS sem URL:', first?.uploadError)
    if (first?.uploadError) throw new Error(first.uploadError)
  } catch (error) {
    console.error('Falha no upload do banner de notícias:', error)
    if (isAnimatedOrVideo(file)) {
      throw error instanceof Error ? error : new Error('Não foi possível enviar o vídeo ou GIF.')
    }
  }

  if (isAnimatedOrVideo(file)) {
    throw new Error('Não foi possível enviar este ficheiro. Tente JPEG, PNG ou um vídeo mais leve.')
  }

  const embedded = await processImage(file, { ratio: 790 / 150, quality: 0.88 })
  return { url: embedded, mediaType: 'image' }
}
